import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Download, Loader2, FileSpreadsheet, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import * as XLSX from "xlsx";

function buildQueryLetter1(selectFields: string) {
  return supabase
    .from("mailing_records")
    .select(selectFields)
    .eq("times_mailed", 0)
    .or("business_name.is.null,business_name.eq.")
    .or("state.eq.MI,origin_state.eq.MI")
    .eq("mailing_status", "active")
    .not("recorded_date", "is", null)
    .lte("recorded_date", "2025-09-01")
    .order("last_name", { ascending: true })
    .order("first_name", { ascending: true });
}

function buildQueryLetter3(selectFields: string) {
  return supabase
    .from("mailing_records")
    .select(selectFields)
    .eq("times_mailed", 2)
    .eq("mailing_status", "active")
    .or("response.is.null,response.eq.")
    .not("last_mailed_date", "is", null)
    .order("last_mailed_date", { ascending: true })
    .order("last_name", { ascending: true });
}

export function MailingQueryExport() {
  const [exporting, setExporting] = useState(false);
  const [commitDialogOpen, setCommitDialogOpen] = useState(false);
  const [campaignName, setCampaignName] = useState("");
  const [mailDate, setMailDate] = useState(new Date().toISOString().split("T")[0]);
  const [committing, setCommitting] = useState(false);

  // Letter 3 state
  const [exporting3, setExporting3] = useState(false);
  const [commitDialog3Open, setCommitDialog3Open] = useState(false);
  const [campaignName3, setCampaignName3] = useState("");
  const [mailDate3, setMailDate3] = useState(new Date().toISOString().split("T")[0]);
  const [committing3, setCommitting3] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      let allRecords: any[] = [];
      let from = 0;
      const pageSize = 1000;
      
      while (true) {
        const { data, error } = await buildQueryLetter1("first_name, last_name, street_address, city, state, zip_code, county")
          .range(from, from + pageSize - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        allRecords = [...allRecords, ...data];
        if (data.length < pageSize) break;
        from += pageSize;
      }

      if (allRecords.length === 0) {
        toast.error("No records found matching criteria");
        setExporting(false);
        return;
      }

      const toProperCase = (s: string | null) => {
        if (!s) return "";
        return s.replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.substring(1).toLowerCase());
      };

      const rows = allRecords.map((r) => ({
        "First Name": toProperCase(r.first_name),
        "Last Name": toProperCase(r.last_name),
        "Street Address": r.street_address || "",
        "City": toProperCase(r.city),
        "State": (r.state || "").toUpperCase(),
        "Zip Code": (r.zip_code || "").split("-")[0],
        "County": toProperCase(r.county),
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [
        { wch: 18 }, { wch: 22 }, { wch: 30 }, { wch: 18 },
        { wch: 8 }, { wch: 12 }, { wch: 16 },
      ];

      XLSX.utils.book_append_sheet(wb, ws, "MI Never Mailed");
      XLSX.writeFile(wb, `MI_Never_Mailed_Individuals_${new Date().toISOString().split("T")[0]}.xlsx`);
      toast.success(`Exported ${rows.length} records to Excel`);
    } catch (err) {
      console.error(err);
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleCommitToMail = async () => {
    if (!campaignName.trim()) {
      toast.error("Please enter a campaign name");
      return;
    }
    setCommitting(true);
    try {
      let allIds: string[] = [];
      let from = 0;
      const pageSize = 1000;

      while (true) {
        const { data, error } = await buildQueryLetter1("id, times_mailed")
          .range(from, from + pageSize - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        allIds = [...allIds, ...data.map((r: any) => r.id)];
        if (data.length < pageSize) break;
        from += pageSize;
      }

      if (allIds.length === 0) {
        toast.error("No records match criteria");
        setCommitting(false);
        return;
      }

      const batchSize = 500;
      for (let i = 0; i < allIds.length; i += batchSize) {
        const batch = allIds.slice(i, i + batchSize);

        const historyEntries = batch.map((id) => ({
          mailing_record_id: id,
          campaign_name: campaignName.trim(),
          mailed_date: mailDate,
          mailing_round: 1,
        }));

        const { error: historyError } = await supabase
          .from("mailing_history")
          .insert(historyEntries);
        if (historyError) throw historyError;

        for (const id of batch) {
          const { error: updateError } = await supabase
            .from("mailing_records")
            .update({
              times_mailed: (await supabase.from("mailing_records").select("times_mailed").eq("id", id).single()).data?.times_mailed + 1 || 1,
              last_mailed_date: mailDate,
            })
            .eq("id", id);
          if (updateError) throw updateError;
        }
      }

      toast.success(`Committed ${allIds.length} records to "${campaignName.trim()}"`);
      setCommitDialogOpen(false);
      setCampaignName("");
    } catch (err) {
      console.error(err);
      toast.error("Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  const handleExportLetter3 = async () => {
    setExporting3(true);
    try {
      let allRecords: any[] = [];
      let from = 0;
      const pageSize = 1000;

      while (true) {
        const { data, error } = await buildQueryLetter3("first_name, last_name, street_address, city, state, zip_code, county, last_mailed_date")
          .range(from, from + pageSize - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        allRecords = [...allRecords, ...data];
        if (data.length < pageSize) break;
        from += pageSize;
      }

      if (allRecords.length === 0) {
        toast.error("No records found matching Letter 3 criteria");
        setExporting3(false);
        return;
      }

      const toProperCase = (s: string | null) => {
        if (!s) return "";
        return s.replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.substring(1).toLowerCase());
      };

      const rows = allRecords.map((r) => ({
        "First Name": toProperCase(r.first_name),
        "Last Name": toProperCase(r.last_name),
        "Street Address": r.street_address || "",
        "City": toProperCase(r.city),
        "State": (r.state || "").toUpperCase(),
        "Zip Code": (r.zip_code || "").split("-")[0],
        "County": toProperCase(r.county),
        "Last Mailed": r.last_mailed_date || "",
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [
        { wch: 18 }, { wch: 22 }, { wch: 30 }, { wch: 18 },
        { wch: 8 }, { wch: 12 }, { wch: 16 }, { wch: 14 },
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Letter 3");
      XLSX.writeFile(wb, `Letter_3_Mailed_Twice_${new Date().toISOString().split("T")[0]}.xlsx`);
      toast.success(`Exported ${rows.length} Letter 3 records to Excel`);
    } catch (err) {
      console.error(err);
      toast.error("Export failed");
    } finally {
      setExporting3(false);
    }
  };

  const handleCommitLetter3 = async () => {
    if (!campaignName3.trim()) {
      toast.error("Please enter a campaign name");
      return;
    }
    setCommitting3(true);
    try {
      let allIds: string[] = [];
      let from = 0;
      const pageSize = 1000;

      while (true) {
        const { data, error } = await buildQueryLetter3("id, times_mailed")
          .range(from, from + pageSize - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        allIds = [...allIds, ...data.map((r: any) => r.id)];
        if (data.length < pageSize) break;
        from += pageSize;
      }

      if (allIds.length === 0) {
        toast.error("No records match Letter 3 criteria");
        setCommitting3(false);
        return;
      }

      const batchSize = 500;
      for (let i = 0; i < allIds.length; i += batchSize) {
        const batch = allIds.slice(i, i + batchSize);

        const historyEntries = batch.map((id) => ({
          mailing_record_id: id,
          campaign_name: campaignName3.trim(),
          mailed_date: mailDate3,
          mailing_round: 3,
        }));

        const { error: historyError } = await supabase
          .from("mailing_history")
          .insert(historyEntries);
        if (historyError) throw historyError;

        for (const id of batch) {
          const { error: updateError } = await supabase
            .from("mailing_records")
            .update({
              times_mailed: (await supabase.from("mailing_records").select("times_mailed").eq("id", id).single()).data?.times_mailed + 1 || 1,
              last_mailed_date: mailDate3,
            })
            .eq("id", id);
          if (updateError) throw updateError;
        }
      }

      toast.success(`Committed ${allIds.length} Letter 3 records to "${campaignName3.trim()}"`);
      setCommitDialog3Open(false);
      setCampaignName3("");
    } catch (err) {
      console.error(err);
      toast.error("Commit failed");
    } finally {
      setCommitting3(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Michigan — Never Mailed Individuals (Recorded ≤ 9/1/2025)
          </CardTitle>
          <CardDescription>
            Individual records in Michigan that have never been mailed, with recorded dates on or before September 1, 2025.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 items-center flex-wrap">
            <Button onClick={handleExport} disabled={exporting} className="gap-2">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {exporting ? "Exporting..." : "Download Excel"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setCommitDialogOpen(true)}
              className="gap-2"
            >
              <CheckCircle2 className="h-4 w-4" />
              Commit to Mail
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={commitDialogOpen} onOpenChange={setCommitDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Commit to Mail</DialogTitle>
            <DialogDescription>
              This will mark matching MI never-mailed individual records as mailed, increment their mail count, and create history entries.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Campaign Name</Label>
              <Input
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="e.g. March 2026 – MI Land Contracts"
              />
            </div>
            <div>
              <Label>Mail Date</Label>
              <Input
                type="date"
                value={mailDate}
                onChange={(e) => setMailDate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommitDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCommitToMail} disabled={committing} className="gap-2">
              {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {committing ? "Committing..." : "Commit Records"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Letter 3 Card */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Letter 3 — Mailed Twice, No Response (Oldest First)
          </CardTitle>
          <CardDescription>
            Records mailed exactly 2 times with no response, sorted by oldest last-mailed date first — ready for a third mailing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 items-center flex-wrap">
            <Button onClick={handleExportLetter3} disabled={exporting3} className="gap-2">
              {exporting3 ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {exporting3 ? "Exporting..." : "Download Letter 3 Excel"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setCommitDialog3Open(true)}
              className="gap-2"
            >
              <CheckCircle2 className="h-4 w-4" />
              Commit Letter 3 to Mail
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={commitDialog3Open} onOpenChange={setCommitDialog3Open}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Commit Letter 3 to Mail</DialogTitle>
            <DialogDescription>
              This will mark all mailed-twice / no-response records as mailed a third time, increment their mail count, and create history entries.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Campaign Name</Label>
              <Input
                value={campaignName3}
                onChange={(e) => setCampaignName3(e.target.value)}
                placeholder="e.g. March 2026 – Letter 3"
              />
            </div>
            <div>
              <Label>Mail Date</Label>
              <Input
                type="date"
                value={mailDate3}
                onChange={(e) => setMailDate3(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommitDialog3Open(false)}>Cancel</Button>
            <Button onClick={handleCommitLetter3} disabled={committing3} className="gap-2">
              {committing3 ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {committing3 ? "Committing..." : "Commit Records"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
